import React, { Component } from 'react'
import { Spacer, Box, TextArea, KeyValueRow, Input, Select, Icons } from 'components'
import styled from '@emotion/styled'
import Button from 'components/Button'
import AppStore from 'AppStore'
import { T, __ } from 'translations/i18n'
import { RepairListEntity, TmrPrinter } from 'api/types'
import RepairReasons from 'api/RepairReasons'
import RepairManagers from 'api/RepairManagers'
import { showToast } from 'shared/utils'
import Modal from './Modal'

interface Props {
  title: string
  subtitle?: string
  isOpen?: boolean
  isRepairMode?: boolean
  onConfirm: (
    note: string,
    printer: TmrPrinter,
    quantity: string,
    weight?: string,
    sizeH?: string,
    sizeL?: string,
    sizeP?: string,
    repairReasonDescription?: string,
    repairManagerDescription?: string
  ) => void
  onClose: () => void
  style?: any
}
interface State {
  note: string
  quantity: string
  printer?: TmrPrinter
  weight?: string
  sizeH?: string
  sizeL?: string
  sizeP?: string
  isSizeRequired: boolean
  repairReasons: RepairListEntity[]
  repairManagers: RepairListEntity[]
  selectedRepairReason?: RepairListEntity
  selectedRepairManager?: RepairListEntity
  loadingRepairOptions: boolean
}
export default class PrintNotesModal extends Component<Props, State> {
  state: State = {
    note: '',
    quantity: AppStore.defaultPrintQuantity ?? '1',
    sizeH: AppStore.defaultSizeH,
    sizeL: AppStore.defaultSizeL,
    sizeP: AppStore.defaultSizeP,
    weight: AppStore.defaultWeight,
    printer: AppStore.defaultPrinter,
    isSizeRequired: false,
    repairReasons: [],
    repairManagers: [],
    loadingRepairOptions: !!this.props.isRepairMode,
  }

  componentDidMount() {
    if (this.props.isRepairMode) {
      this.loadRepairOptions()
    }
  }

  loadRepairOptions = async () => {
    this.setState({ loadingRepairOptions: true })
    try {
      const [repairReasons, repairManagers] = await Promise.all([
        RepairReasons.search<RepairListEntity>({ limit: 500, active: true, orderBy: 'DESCRIPTION' }),
        RepairManagers.search<RepairListEntity>({ limit: 500, active: true, orderBy: 'DESCRIPTION' }),
      ])
      this.setState({
        repairReasons: repairReasons ?? [],
        repairManagers: repairManagers ?? [],
        loadingRepairOptions: false,
      })
    } catch (error) {
      this.setState({ loadingRepairOptions: false })
      showToast({
        title: __(T.error.error),
        description: error?.message ?? __(T.error.generic_error),
        status: 'error',
      })
    }
  }

  handleConfirm = () => {
    const { isRepairMode, onConfirm } = this.props
    const {
      note,
      printer,
      quantity,
      weight,
      sizeH,
      sizeL,
      sizeP,
      selectedRepairReason,
      selectedRepairManager,
      loadingRepairOptions,
    } = this.state

    if (isRepairMode && loadingRepairOptions) return

    if (!printer) {
      return showToast({
        title: __(T.error.error),
        description: __(T.error.printer_not_found),
        status: 'error',
      })
    }

    if (isRepairMode && !selectedRepairReason?.description) {
      return showToast({
        title: __(T.error.error),
        description: __(T.error.repair_reason_required),
        status: 'error',
      })
    }

    if (isRepairMode && !selectedRepairManager?.description) {
      return showToast({
        title: __(T.error.error),
        description: __(T.error.repair_manager_required),
        status: 'error',
      })
    }

    return onConfirm(
      note,
      printer,
      quantity,
      weight,
      sizeH,
      sizeL,
      sizeP,
      selectedRepairReason?.description,
      selectedRepairManager?.description
    )
  }

  renderRepairSelect = (
    options: RepairListEntity[],
    placeholder: string,
    onSelect: (item?: RepairListEntity) => void
  ) => {
    const { loadingRepairOptions } = this.state
    return (
      <Box row vcenter style={{ width: '100%', height: 64 }}>
        <Box style={{ flex: 1, minWidth: 0, height: 64 }}>
          <Select
            options={options}
            placeholder={placeholder}
            onSelect={onSelect}
            disabled={loadingRepairOptions}
            config={{ value: 'id', label: 'description' }}
          />
        </Box>
        <Box
          center
          style={{ width: 50, height: 64, marginLeft: 10, flexShrink: 0, overflow: 'hidden' }}
        >
          {loadingRepairOptions && <Icons.Loader style={{ width: 40, height: 40 }} />}
        </Box>
      </Box>
    )
  }

  render() {
    const { isOpen, onClose, title, subtitle, isRepairMode } = this.props
    const { repairReasons, repairManagers, loadingRepairOptions } = this.state
    return (
      <Modal visible={isOpen ?? true} size="2xl" onClose={onClose}>
        <Box>
          <Title>{title}</Title>
          {subtitle && <Value>{subtitle}</Value>}
          <KeyValueRow
            variant="grey"
            label={__(T.misc.printer)}
            value={this.state.printer?.description}
            optionModal={{
              onSelect: (printers) => {
                AppStore.saveDefaultPrinter(printers[0])
                this.setState({ printer: printers[0] })
              },
              field: 'description',
              options: AppStore.printers ?? [],
              title: __(T.titles.select_printer),
              selected: [this.state.printer],
            }}
          />
          <Spacer />
          {isRepairMode && (
            <>
              {this.renderRepairSelect(repairReasons, __(T.placeholder.repair_reason), (item) =>
                this.setState({ selectedRepairReason: item })
              )}
              <Spacer />
              {this.renderRepairSelect(repairManagers, __(T.placeholder.repair_manager), (item) =>
                this.setState({ selectedRepairManager: item })
              )}
              <Spacer />
            </>
          )}
          <TextArea
            textAreaStyle={{ height: 170 }}
            placeholder="Note da mandare in stampa"
            onChange={(note) => this.setState({ note })}
            maxLength={90}
          />
          <Input
            defaultValue={this.state.quantity}
            placeholder={__(T.placeholder.print_quantity)}
            onChange={(quantity) => this.setState({ quantity })}
            required
          />
          <Spacer />
          <Box flex row center>
            <Input
              defaultValue={this.state.sizeH}
              label="H"
              onChange={(sizeH) => {
                this.setState({ sizeH })
              }}
            />
            <Value style={{ paddingRight: 9, paddingLeft: 2 }}>X</Value>
            <Input
              defaultValue={this.state.sizeL}
              label="L"
              onChange={(sizeL) => {
                this.setState({ sizeL })
              }}
            />
            <Value style={{ paddingRight: 9, paddingLeft: 2 }}>X</Value>
            <Input
              defaultValue={this.state.sizeP}
              label="P"
              onChange={(sizeP) => {
                this.setState({ sizeP })
              }}
            />
          </Box>
          <Spacer />
          <Input
            defaultValue={this.state.weight}
            placeholder={__(T.placeholder.insert_weight)}
            onChange={(weight) => this.setState({ weight })}
            required
          />
          <Box center row mt={15}>
            <Button style={{ flex: 0 }} title="Annulla" onClick={onClose} variant="default" />
            <Spacer />
            <Button
              style={{ flex: 1 }}
              title="Conferma collo"
              onClick={this.handleConfirm}
              loading={isRepairMode && loadingRepairOptions}
            />
          </Box>
        </Box>
      </Modal>
    )
  }
}

const Title = styled.label`
  font-weight: bold;
  margin-bottom: 5px;
  font-size: 18px;
`

const Value = styled.label`
  font-weight: 700;
  margin-bottom: 5px;
  font-size: 20px;
  overflow: hidden;
`
